// src/pages/ManagedAuthorDetailPage.jsx
// Phase P3: Author Detail Page with Titles Section
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import PublisherNav from '../components/PublisherNav';
import TTSStatusBanner from '../components/TTSStatusBanner';
import { User, ArrowLeft, Plus, BookOpen, Edit, FileText, Image, CheckCircle, Clock, AlertCircle, Archive, Upload } from 'lucide-react';
import UniversalFooter from '../components/UniversalFooter';
import { toast } from 'sonner';
import { BACKEND_URL as backendUrl } from '@/config/api';

const ManagedAuthorDetailPage = () => {
  const { authorId } = useParams();
  const navigate = useNavigate();
  const [author, setAuthor] = useState(null);
  const [titles, setTitles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imprintLogoUrl, setImprintLogoUrl] = useState(null);
  // backendUrl imported from centralized config as BACKEND_URL

  useEffect(() => {
    fetchAuthorAndTitles();
  }, [authorId]);

  const fetchAuthorAndTitles = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      
      // Fetch author details
      const authorRes = await fetch(`${backendUrl}/api/publisher/authors/${authorId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!authorRes.ok) {
        toast.error('Author not found');
        navigate('/publisher/authors');
        return;
      }
      
      const authorData = await authorRes.json();
      setAuthor(authorData);
      setImprintLogoUrl(authorData.headshot_fallback_url);
      
      // Fetch titles for this author
      const titlesRes = await fetch(`${backendUrl}/api/publisher/titles?publisher_author_id=${authorId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (titlesRes.ok) {
        const titlesData = await titlesRes.json();
        setTitles(titlesData.titles || []);
      }
    } catch (error) {
      console.error('Error fetching author:', error);
      toast.error('Failed to load author');
    } finally {
      setLoading(false);
    }
  };

  const getHeadshotUrl = () => {
    if (author?.headshot_image_url) {
      return `${backendUrl}${author.headshot_image_url}?t=${Date.now()}`;
    }
    if (imprintLogoUrl) {
      return `${backendUrl}${imprintLogoUrl}?t=${Date.now()}`;
    }
    return null;
  };

  const getCoverUrl = (title) => {
    if (title.cover_image_url) {
      return `${backendUrl}${title.cover_image_url}?t=${Date.now()}`;
    }
    return null;
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { icon: FileText, color: 'text-slate-400 bg-slate-800', label: 'Draft' },
      processing: { icon: Clock, color: 'text-amber-400 bg-amber-900/30', label: 'Processing' },
      active: { icon: CheckCircle, color: 'text-emerald-400 bg-emerald-900/30', label: 'Active' },
      archived: { icon: Archive, color: 'text-slate-500 bg-slate-800', label: 'Archived' },
      failed: { icon: AlertCircle, color: 'text-red-400 bg-red-900/30', label: 'Failed' }
    };
    const badge = badges[status] || badges.draft;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-50">
        <PublisherNav />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-t-blue-500 border-r-transparent border-b-indigo-500 border-l-transparent"></div>
        </div>
      </div>
    );
  }

  const headshotUrl = getHeadshotUrl();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      <PublisherNav />
      
      {/* TTS Status Banner - shows when TTS jobs are active */}
      <TTSStatusBanner />
      
      <main className="mx-auto max-w-6xl px-4 py-8">
        {/* Back button */}
        <Link
          to="/publisher/authors"
          className="flex items-center gap-2 text-slate-400 hover:text-slate-200 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Authors
        </Link>

        {/* Author Header */}
        <div className="rounded-2xl border border-slate-700 bg-slate-900/50 p-6 mb-8">
          <div className="flex flex-col sm:flex-row items-start gap-6">
            {/* Headshot */}
            {headshotUrl ? (
              <img
                src={headshotUrl}
                alt={author.pen_name}
                className="w-24 h-24 rounded-xl object-cover border border-slate-600"
              />
            ) : (
              <div className="w-24 h-24 rounded-xl bg-slate-800 border border-slate-600 flex items-center justify-center">
                <User className="w-10 h-10 text-slate-500" />
              </div>
            )}
            
            {/* Info */}
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-slate-100">{author.pen_name}</h1>
              {author.real_name && (
                <p className="text-sm text-slate-400 mt-1">
                  Internal: {author.real_name}
                </p>
              )}
              {author.about_text && (
                <p className="text-sm text-slate-300 mt-3 line-clamp-3 whitespace-pre-wrap">
                  {author.about_text.substring(0, 200)}...
                </p>
              )}
              <div className="mt-4 flex gap-3">
                <Link
                  to={`/publisher/authors/${authorId}/console`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-sm text-white font-medium transition-colors"
                >
                  <Upload className="w-4 h-4" />
                  Open Console
                </Link>
                <Link
                  to={`/publisher/authors/${authorId}/edit`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm text-slate-300 transition-colors"
                >
                  <Edit className="w-4 h-4" />
                  Edit Profile
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Works Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-indigo-600/20 p-2">
                <BookOpen className="w-5 h-5 text-indigo-400" />
              </div>
              <h2 className="text-xl font-semibold">Works</h2>
              <span className="text-sm text-slate-400">({titles.length})</span>
            </div>
            <button
              onClick={() => navigate(`/publisher/authors/${authorId}/titles/new`)}
              className="inline-flex items-center gap-2 rounded-full bg-indigo-600 hover:bg-indigo-500 px-5 py-2 text-sm font-semibold text-white transition-colors"
            >
              <Upload className="w-4 h-4" />
              Upload New Manuscript
            </button>
          </div>

          {titles.length === 0 ? (
            <div className="rounded-2xl border border-slate-700 bg-slate-900/50 p-12 text-center">
              <div className="mx-auto w-14 h-14 rounded-full bg-indigo-600/20 flex items-center justify-center mb-4">
                <Upload className="w-7 h-7 text-indigo-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-200 mb-2">No Works Yet</h3>
              <p className="text-slate-400 max-w-sm mx-auto mb-6">
                Upload a manuscript to create the first work for this author.
              </p>
              <button
                onClick={() => navigate(`/publisher/authors/${authorId}/titles/new`)}
                className="inline-flex items-center gap-2 rounded-full bg-indigo-600 hover:bg-indigo-500 px-6 py-2.5 text-sm font-semibold text-white transition-colors"
              >
                <Upload className="w-4 h-4" />
                Upload First Manuscript
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {titles.map((title) => {
                const coverUrl = getCoverUrl(title);
                return (
                  <div
                    key={title.id}
                    className="rounded-2xl border border-slate-700 bg-slate-900/50 overflow-hidden hover:border-slate-600 transition-colors"
                  >
                    {/* Cover */}
                    <div className="aspect-[3/4] bg-slate-800 relative">
                      {coverUrl ? (
                        <img
                          src={coverUrl}
                          alt={title.title_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Image className="w-12 h-12 text-slate-600" />
                        </div>
                      )}
                      <div className="absolute top-2 right-2">
                        {getStatusBadge(title.status)}
                      </div>
                    </div>
                    
                    {/* Info */}
                    <div className="p-4">
                      <h3 className="font-semibold text-slate-100 truncate">{title.title_name}</h3>
                      {title.subtitle && (
                        <p className="text-sm text-slate-400 truncate mt-0.5">{title.subtitle}</p>
                      )}
                      <Link
                        to={`/publisher/titles/${title.id}/edit`}
                        className="mt-3 flex items-center justify-center gap-2 w-full rounded-lg bg-slate-800 hover:bg-slate-700 px-3 py-2 text-sm text-slate-300 transition-colors"
                      >
                        <Edit className="w-3.5 h-3.5" />
                        Manage Title
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
      <UniversalFooter />
    </div>
  );
};

export default ManagedAuthorDetailPage;
